import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tag } from "entities/shared/tags.types";
import { useRequestCallbacks } from "shared/api/lib/api.hooks";

import { tagsService } from "../tags.api";

type TagsRequestFunc = () => void;
type ResetTagsQueryFunc = () => Promise<void>;
type RemoveTagsCacheFunc = () => void;

type UseSeaGetTagsQueryReturnType = {
  seaTagsRequest: TagsRequestFunc;
  resetSeaTagsQuery: ResetTagsQueryFunc;
  removeSeaTagsCache: RemoveTagsCacheFunc;
  seaTagsData: Tag[] | undefined;
  isSeaTagsSuccess: boolean;
  isSeaTagsError: boolean;
  isSeaTagsLoading: boolean;
  isSeaTagsFetching: boolean;
};

const TAGS_QUERY_KEY = "seaTagsQuery";

export const useSeaGetTagsQuery = (): UseSeaGetTagsQueryReturnType => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<boolean>(false);

  const meta = useQuery<Tag[], Error, Tag[]>({
    queryKey: [TAGS_QUERY_KEY],
    queryFn: () => tagsService.getTags(),
    enabled,
  });

  const { data, isError, isLoading, isSuccess, isFetching } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<Tag[] | undefined, Error>(meta);

  const resetSeaTagsQuery: ResetTagsQueryFunc = () => {
    return queryClient.resetQueries({ queryKey: [TAGS_QUERY_KEY] });
  };

  const removeSeaTagsCache: RemoveTagsCacheFunc = () => {
    queryClient.removeQueries({ queryKey: [TAGS_QUERY_KEY] });
  };

  const seaTagsRequest = useCallback<TagsRequestFunc>(() => {
    setRequestCallbacks({
      onSettled: () => {
        setEnabled(false);
      },
    });
    setEnabled(true);
  }, []);

  return {
    seaTagsRequest,
    seaTagsData: data,
    isSeaTagsSuccess: isSuccess,
    isSeaTagsError: isError,
    isSeaTagsLoading: isLoading,
    isSeaTagsFetching: isFetching,
    removeSeaTagsCache,
    resetSeaTagsQuery,
  };
};
